import type { UserResource } from '@clerk/shared/types'
import type { StcsComp } from '~/protos/gen/stcs_comp.entity'
import type { StcsCompAccMapping } from '~/protos/gen/stcs_comp_acc_mapping.entity'
import type { StcsCompActualsResValues } from '~/protos/gen/stcs_comp_actuals_res_values.entity'
import type { StcsCompFssFlat } from '~/protos/gen/stcs_comp_fss_flat.entity'
import type { StcsCompKpiCfg } from '~/protos/gen/stcs_comp_kpi_cfg.entity'
import type { StcsCompKpiResValues } from '~/protos/gen/stcs_comp_kpi_res_values.entity'
import type { StcsCompPlanResValues } from '~/protos/gen/stcs_comp_plan_res_values.entity'
import type { StcsOrg } from '~/protos/gen/stcs_org.entity'
import type { StcsOrgFsdFlat } from '~/protos/gen/stcs_org_fsd_flat.entity'
import type { StcsWappUser } from '~/protos/gen/stcs_user.entity'
import type { ParsedWappSettings } from '~/types/app-settings'
import type { FinancialsGridDataAvailablePlanList } from '~/types/stcs_fpna_res_values.entity'

export type SectionKey = 'financials' | 'plan' | 'analysis' | 'dashboard' | 'settings' | 'data-management'
export type DisplayMode = 'monthly' | 'yearly' | 'diff'
export type FstmtView = 'pnl' | 'revenues' | 'cf_n_debt' | 'nwc' | 'bs' | 'kpis' | 'accounts' | 'cf'
export type KpiRequestKey = 'res' | 'cfg'

export interface KpiRequestStatus {
  loading: boolean
  settled: boolean
  error?: string
}

export type KpiRequestStatusMap = Partial<Record<KpiRequestKey, KpiRequestStatus>>

export interface WappCompData {
  comp_url: string
  has_data: boolean
  ref_comp_actuals_res_fstmt?: StcsCompFssFlat
  ref_comp_actuals_res_values?: StcsCompActualsResValues
  ref_comp_plan_res_values?: StcsCompPlanResValues
  ref_comp: StcsComp
  ui_map: Record<SectionKey, ViewMap>
  // Plan management - company-specific
  availablePlanList: FinancialsGridDataAvailablePlanList[]
  // Plan-specific data storage
  ref_comp_plans?: { [planId: string]: { ref_comp_plan_res_values?: StcsCompPlanResValues } }
  ref_comp_rf_plans?: { [planId: string]: { ref_comp_plan_rf_res_values?: StcsCompPlanResValues } }
  // KPI-specific data storage
  ref_comp_kpis_res?: StcsCompKpiResValues
  ref_comp_kpis_cfg?: StcsCompKpiCfg
  kpi_requests?: KpiRequestStatusMap
  ref_comp_acc_mapping?: StcsCompAccMapping | undefined
}

export interface ViewMap {
  // old name: fpna_page_id
  section_key: SectionKey
  // old name: fpna_scope_id
  display_mode: DisplayMode
  // old name: fpna_fstmt_id
  fstmt_view: FstmtView
  // old name: fpna_scope_params
  scope_params: FpnaScopeParams
  // comparison view for diff mode
  comparision_view?: ComparisionViewProps
  // Dashboard-specific parameters
  comparison?: 'vs-plan' | 'vs-ly'
  delta?: 'absolute' | 'percentage'
  period?: 'ytd' | 'lm'
}

export interface UrlQueryKeys {
  actuals: string
  plan: string
  range: string
}

export interface FpnaScopeParams {
  plan_date_str: string
  actuals_date_str: string
  range_date_str: string
}

export interface ComparisionViewProps {
  col_1?: {
    range_date_str?: string
    is_actual?: boolean
    label?: string
  }
  col_2?: {
    range_date_str?: string
    is_actual?: boolean
    label?: string
  }
}

export interface WappNotificationPayload {
  message: string
  type: 'info' | 'task' | 'warn' | 'error'
  link?: string
  date_time_str: string
  read: boolean
}

export const default_ui_map: Record<SectionKey, ViewMap> = {
  financials: {
    section_key: 'financials',
    display_mode: 'monthly',
    fstmt_view: 'pnl',
    scope_params: {
      plan_date_str: '',
      actuals_date_str: '',
      range_date_str: '',
    },
  },
  plan: {
    section_key: 'plan',
    display_mode: 'monthly',
    fstmt_view: 'pnl',
    scope_params: {
      plan_date_str: '',
      actuals_date_str: '',
      range_date_str: '',
    },
  },
  analysis: {
    section_key: 'analysis',
    display_mode: 'monthly',
    fstmt_view: 'pnl',
    scope_params: {
      plan_date_str: '',
      actuals_date_str: '',
      range_date_str: '',
    },
  },
  dashboard: {
    section_key: 'dashboard',
    display_mode: 'monthly',
    fstmt_view: 'pnl',
    comparison: 'vs-plan',
    delta: 'absolute',
    period: 'ytd',
    scope_params: {
      plan_date_str: '',
      actuals_date_str: '',
      range_date_str: '',
    },
  },
  settings: {
    section_key: 'settings',
    display_mode: 'monthly',
    fstmt_view: 'pnl',
    scope_params: {
      plan_date_str: '',
      actuals_date_str: '',
      range_date_str: '',
    },
  },
  'data-management': {
    section_key: 'data-management',
    display_mode: 'monthly',
    fstmt_view: 'pnl',
    scope_params: {
      plan_date_str: '',
      actuals_date_str: '',
      range_date_str: '',
    },
  },
}

/**
 * Represents the context data for a user's session and selection state.
 *
 * This interface is used to maintain and track the state of the current user session
 * and their active selections within the application.
 *
 * @property {UserResource} idp_user - The user information provided by the identity provider upon a session change.
 * @property {StcsWappUser} ref_user - The reference user data that changes when the session changes.
 * @property {Object} ref_org_map - A mapping of organization identifiers to organization data objects. This map updates when there's a change in route or user interface selection.
 * @property {Object} ref_comp_map - A mapping of component identifiers to component data objects. This map updates when there's a change in route or user interface selection.
 * @property {boolean} isLoadingUser - Indicates whether the user data is currently loading.
 * @property {string} active_org_id - The identifier of the currently active organization.
 * @property {string} active_comp_id - The identifier of the currently active component.
 */
export interface CtxData {
  idp_user: UserResource // OnSessionChange
  ref_user: StcsWappUser // OnSessionChange
  ref_org_map: { [org_id: string]: StcsOrg } // OnSelectionChange: onRoute | onUiSelection
  ref_comp_map: { [comp_id: string]: WappCompData } // OnSelectionChange: onRoute | onUiSelection
  ref_fsd_map: { [org_id: string]: StcsOrgFsdFlat } // OnSelectionChange: onRoute | onUiSelection
  ref_app_settings_map: { [org_id: string]: ParsedWappSettings } // App settings per organization (parsed objects)
  isLoadingUser: boolean
  active_org_id: string
  active_comp_id: string
  active_section: SectionKey
  appSettingLoading: boolean
}
